/**
 * Soft-tissue regions — ADR-038 J2 (the jiggle arc after tails): **where on
 * this body a breast, the belly and the glutes are, measured from the flesh
 * and the rig, or taken from a zone a person declared.**
 *
 * ONE responsibility: turn a subject (bind-world positions, faces, the skin,
 * the mapped joints) into up to five REGIONS — LeftBreast, RightBreast, Belly,
 * LeftGlute, RightGlute — each a vertex set with an apex (the most prominent
 * flesh), a root (the base of the mass: its attachment ring's centroid pushed
 * back into the body — see THE ROOT below), a per-vertex feather (a plateau:
 * 0 on the boundary, 1 across the mass) and a confidence tier. No bones, no weights, no names beyond the
 * region ids: the graft and the weights are `leaf-bones.ts`.
 *
 * THE BODY FRAME comes from the mapped rig, not the mesh: up is the rig's up;
 * the lateral axis runs from the right hip joint to the left; forward is
 * lateral × up (checked against the toes — a rig whose toes point backward
 * refuses rather than putting the breasts on the back). Heights are fractions
 * of the TORSO T = shoulder-joint height − hip-joint height, so every
 * threshold scales with the character, whatever its units or proportions.
 * Depth is measured from the spine line (the mapped chain Hips → Spine2,
 * interpolated at the vertex's height), positive forward.
 *
 * DETECTION (cheap, offered never forced — the stub rule):
 *  - BREAST (per side): the most anterior torso-owned flesh either side of the
 *    midline between 0.45 T and 0.95 T above the hips (below the clavicles,
 *    above the ribcage bottom). Its PROMINENCE is the apex depth minus the
 *    shallowest depth in the column below it (the inframammary fold): a
 *    female chest shelves, a male chest slopes.
 *  - GLUTE (per side): the most posterior torso- or thigh-owned flesh either
 *    side of the cleft between 0.45 T below and 0.05 T above the hip joints
 *    (the waist belt above them is not a buttock), never loose flesh (a coat
 *    tail — see `looseVertices`); prominence against the lumbar hollow above.
 *  - BELLY: the most anterior torso-owned flesh near the midline between the
 *    belt line and the ribcage (0.15–0.5 T); prominence against the column
 *    above it up to the ribcage.
 *  The region is the flesh inside an ellipsoid around the apex (lateral radius
 *  reaching the midline, vertical radius from the fold, depth reaching the
 *  wall), on its own side of the midline, owned as the apex must be, and
 *  never split by connectivity: a garment shell over the flesh is one mass
 *  with it. The LEAF's tip is the region's surface centre (the flesh farthest
 *  from its ring), its root the base of the mass (THE ROOT).
 *
 * A DECLARED ZONE OVERRIDES: its faces ARE the region; the apex is its most
 * prominent vertex in the region's direction. Cloth faces (a zone a person
 * called cloth) are never in a region — the ring where the region meets cloth
 * becomes boundary, so the feather reaches 0 there.
 *
 * THE FEATHER is one number per welded position (UV twins share it, so a seam
 * never tears), and it is a PLATEAU (2026-09-27): f = smoothstep(dB / w), dB
 * the distance to the nearest boundary position, w = {@link SOFT_TISSUE_PLATEAU}
 * × the mass's prominence — 0 on the ring, 1 once a vertex is w inside it, so
 * the MASS moves as one and only its edge blends into the body. (J2's
 * t = dB / (dB + dA) peaked at a single centre vertex: on Stella the breast's
 * share by distance from the centre fell 0.61 / 0.14 / 0.05 — the leaf swung
 * and the mass stayed; on the walrus the belly moved as a point, a "hernia".)
 * A position any non-region face also uses IS the ring.
 *
 * THE ROOT sits at the BASE of the mass: the ring's centroid pushed back into
 * the body, against the mass's outward direction, by
 * {@link SOFT_TISSUE_PIVOT_DEPTH} × the prominence (at most 0.8 of the ring's
 * own depth from the spine line) — the lever a mass of that size swings on,
 * not a point just under the skin (Stella: 2.86 cm root → centre for a
 * 5.36 cm breast).
 *
 * Depends on: zones/unarmatured (Point3, DetectorBone). Depended on by:
 * jiggle/leaf-bones, the app's run (replay) and the Body tab's card.
 */
import type { DetectorBone, Point3 } from '../zones/unarmatured.js';

export const SOFT_TISSUE_REGIONS = ['LeftBreast', 'RightBreast', 'Belly', 'LeftGlute', 'RightGlute'] as const;
export type SoftTissueRegionId = (typeof SOFT_TISSUE_REGIONS)[number];

/**
 * Every region the leaf/spring/surface machinery drives: the five body masses
 * this detector measures, and the HAIR (ADR-039 T2a) — one stiff leaf over a
 * person's confirmed ATTACHED hair region. Hair is never detected here (its
 * region is the Hair card's confirmed zone, `kernel/hair`); it only shares the
 * leaf, the spring and the surface modes, extended with a `hair` kind rather
 * than forked.
 */
export const JIGGLE_REGIONS = [...SOFT_TISSUE_REGIONS, 'Hair'] as const;
export type JiggleRegionId = (typeof JIGGLE_REGIONS)[number];

/** What a region is, independent of side. */
export type SoftTissueKind = 'breast' | 'belly' | 'glute';
/** A body mass's kind, or the hair's (ADR-039 T2a). */
export type JiggleKind = SoftTissueKind | 'hair';
export function kindOfRegion(id: SoftTissueRegionId): SoftTissueKind;
export function kindOfRegion(id: JiggleRegionId): JiggleKind;
export function kindOfRegion(id: JiggleRegionId): JiggleKind {
  return id === 'Hair' ? 'hair' : id.endsWith('Breast') ? 'breast' : id === 'Belly' ? 'belly' : 'glute';
}
/** +1 the character's left, −1 its right, 0 the midline. */
export const sideOfRegion = (id: JiggleRegionId): 1 | -1 | 0 => (id.startsWith('Left') ? 1 : id.startsWith('Right') ? -1 : 0);

/**
 * How sure the measurement is that there is a mass worth a bone here.
 * `strong` = offered as a default-on suggestion; `weak` = offered, not
 * suggested; `none` = not offered (a person can still declare a zone).
 */
export type SoftTissueTier = 'strong' | 'weak' | 'none';

/**
 * Prominence thresholds, as fractions of the torso T. Measured 2026-09-24 on
 * the specimen corpus (`app/src/jiggle/soft-tissue-probe.test.ts`): see the
 * table there. A threshold is a starting point for the eye, not anatomy.
 */
export const SOFT_TISSUE_TIERS: Readonly<Record<SoftTissueKind, { strong: number; weak: number }>> = {
  breast: { strong: 0.1, weak: 0.045 },
  belly: { strong: 0.08, weak: 0 },
  glute: { strong: 0.08, weak: 0.03 },
};

export interface SoftTissueInput {
  /** Bind-world positions, xyz interleaved. */
  readonly positions: ArrayLike<number>;
  /** Triangle list. */
  readonly indices: ArrayLike<number>;
  /** Skeleton bones in SKIN order (so `skin.index` addresses them). */
  readonly bones: readonly DetectorBone[];
  /** Four influences per vertex. */
  readonly skin: { readonly index: ArrayLike<number>; readonly weight: ArrayLike<number> };
  /** BBSA ontology id → rig bone name (the rig's mapping). */
  readonly mapped: Readonly<Record<string, string>>;
  /** The rig's up (default +Y). */
  readonly up?: Point3;
  /** Faces never in a region (cloth zones). */
  readonly excludeFaces?: readonly number[];
  /** Vertices never in a detected region — a tail the body grows or wears (its root is the most posterior flesh at glute height). */
  readonly excludeVertices?: readonly number[];
  /**
   * Flesh the unarmatured detector reads as a SHEET or a TUBE hanging off the
   * body (a coat tail, a skirt flap, a loincloth) — never a glute: at glute
   * height it is the most posterior flesh and the thighs own it, exactly like
   * a buttock (the tiger warrior's coat, measured 2026-09-24).
   */
  readonly looseVertices?: readonly number[];
  /** Declared regions: the zone's faces override the detector. */
  readonly declared?: Partial<Record<SoftTissueRegionId, { readonly zoneName: string; readonly faces: readonly number[] }>>;
}

export interface SoftTissueFrame {
  readonly origin: Point3;
  readonly up: Point3;
  readonly lateral: Point3;
  readonly forward: Point3;
  /** Shoulder-joint height − hip-joint height (world units). */
  readonly torso: number;
  /** Hip joint separation (world units). */
  readonly hipWidth: number;
}

export interface SoftTissueRegion {
  readonly id: SoftTissueRegionId;
  readonly kind: SoftTissueKind;
  /** `measured` = the detector's; `declared` = a zone's faces. */
  readonly source: 'measured' | 'declared';
  readonly zoneName: string | null;
  readonly tier: SoftTissueTier;
  /** Apex depth beyond the wall, world units (breast: over the fold; glute: over the lumbar hollow; belly: over the ribcage column). */
  readonly prominence: number;
  /** prominence / torso. */
  readonly prominenceRatio: number;
  /** The most prominent flesh (bind-world). */
  readonly apex: Point3;
  /** The centroid of the attachment ring — the leaf's pivot, on or inside the body wall. */
  readonly root: Point3;
  /** Region vertices (every welded twin included), ascending. */
  readonly vertices: readonly number[];
  /** The region's faces (all three corners in the region). */
  readonly faces: readonly number[];
  /** Per vertex in `vertices`, the feather f ∈ [0, 1] (1 at the apex, 0 on the ring). */
  readonly feather: readonly number[];
  /** Ring positions (welded) — where the region meets the rest of the body. */
  readonly ringPositions: number;
  /** Faces left out because a zone calls them cloth. */
  readonly clothFacesSkipped: number;
  /** The flesh radius at the apex — the spring's hit radius (half the region's smaller in-plane extent). */
  readonly radius: number;
  /** One sentence: what was measured, or why it is not offered. */
  readonly reading: string;
}

export interface SoftTissueReport {
  readonly frame: SoftTissueFrame | null;
  readonly frameRefusal: string | null;
  /** One entry per region id that could be measured (a `none` tier still reports its reading). */
  readonly regions: readonly SoftTissueRegion[];
  /** Region ids the detector could not measure at all, with why. */
  readonly unmeasured: readonly { readonly id: SoftTissueRegionId; readonly why: string }[];
}

type Owner = 'torso' | 'arm' | 'leg' | 'head' | 'tail' | 'other';

const ARM = /^(Left|Right)(Arm|ForeArm|Hand)/;
const LEG = /^(Left|Right)(UpLeg|Leg|Foot|Toe)/;
const HEAD = /^(Neck|Head|Jaw|LeftEye|RightEye|HeadTop)/;
const TORSO = /^(Hips|Spine|Spine1|Spine2|LeftShoulder|RightShoulder|LeftBreast|RightBreast)$/;

const sub = (a: Point3, b: Point3): Point3 => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a: Point3, b: Point3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: Point3, b: Point3): Point3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const scale = (a: Point3, s: number): Point3 => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const add = (a: Point3, b: Point3): Point3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const norm = (a: Point3): Point3 => {
  const l = Math.hypot(a.x, a.y, a.z);
  return l > 0 ? scale(a, 1 / l) : a;
};
/** The feather's ramp width, as a fraction of the mass's prominence: full share once a vertex is this far inside the ring. */
export const SOFT_TISSUE_PLATEAU = 0.5;
/** How far below its apex a glute region may reach, as a torso fraction (the leaf's tip is the region's centre). */
export const GLUTE_REACH_BELOW = 0.2;
/** How far behind the ring's centroid the leaf's root sits, as a fraction of the prominence. */
export const SOFT_TISSUE_PIVOT_DEPTH = 0.5;

const smoothstep = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

/** The body frame from the mapped joints, or a refusal a person can read. */
export function softTissueFrame(input: Pick<SoftTissueInput, 'bones' | 'mapped' | 'up'>): SoftTissueFrame | string {
  const at = (id: string): Point3 | undefined => {
    const name = input.mapped[id];
    return name === undefined ? undefined : input.bones.find((b) => b.name === name)?.worldPosition;
  };
  const lHip = at('LeftUpLeg');
  const rHip = at('RightUpLeg');
  const lArm = at('LeftArm');
  const rArm = at('RightArm');
  if (lHip === undefined || rHip === undefined) return 'the rig maps no two hip joints (LeftUpLeg / RightUpLeg) — no pelvis to measure the body from';
  if (lArm === undefined || rArm === undefined) return 'the rig maps no two shoulder joints (LeftArm / RightArm) — no chest height to measure from';
  const up = norm(input.up ?? { x: 0, y: 1, z: 0 });
  const across = sub(lHip, rHip);
  // The lateral axis from BOTH girdles: one pair of joints seated a centimetre
  // apart in depth skews the frame (Prince's hips: 6.8°, measured 2026-09-24)
  // and pushes a midline vertex over a side gate.
  const girdles = add(norm(across), norm(sub(lArm, rArm)));
  const lateral = norm(sub(girdles, scale(up, dot(girdles, up))));
  if (Math.hypot(lateral.x, lateral.y, lateral.z) < 0.5) return 'the hip joints coincide — no lateral axis';
  const forward = cross(lateral, up);
  const lFoot = at('LeftFoot');
  const lToe = at('LeftToeBase');
  if (lFoot !== undefined && lToe !== undefined && dot(sub(lToe, lFoot), forward) < 0) {
    return 'the left toes point backward in the rig\'s own left/right — a mirrored rig; its front cannot be trusted';
  }
  const origin = scale(add(lHip, rHip), 0.5);
  const torso = dot(sub(scale(add(lArm, rArm), 0.5), origin), up);
  if (!(torso > 0)) return 'the shoulders are not above the hips';
  return { origin, up, lateral, forward, torso, hipWidth: Math.hypot(across.x, across.y, across.z) };
}

/** Measure (or take from zones) every soft-tissue region of one body. */
export function detectSoftTissue(input: SoftTissueInput): SoftTissueReport {
  const frameOrWhy = softTissueFrame(input);
  if (typeof frameOrWhy === 'string') return { frame: null, frameRefusal: frameOrWhy, regions: [], unmeasured: SOFT_TISSUE_REGIONS.map((id) => ({ id, why: frameOrWhy })) };
  const F = frameOrWhy;
  const T = F.torso;
  const P = input.positions;
  const n = Math.floor(P.length / 3);
  const pos = (v: number): Point3 => ({ x: P[v * 3]!, y: P[v * 3 + 1]!, z: P[v * 3 + 2]! });

  // --- the owner of every vertex, by its dominant bone's nearest mapped ancestor
  const idOf = new Map<string, string>();
  for (const [id, name] of Object.entries(input.mapped)) idOf.set(name, id);
  const byName = new Map(input.bones.map((b) => [b.name, b]));
  const ownerOfBone = input.bones.map((b): Owner => {
    let at: DetectorBone | undefined = b;
    while (at !== undefined) {
      const id = idOf.get(at.name);
      // A soft-tissue leaf's flesh is its parent's (the chest, the waist, the
      // pelvis): a body that already wears one leaf still measures the others.
      if (id !== undefined && !/Jiggle(_End)?$/.test(id)) {
        if (/^Tail\d/.test(id)) return 'tail';
        if (ARM.test(id)) return 'arm';
        if (LEG.test(id)) return 'leg';
        if (HEAD.test(id)) return 'head';
        if (TORSO.test(id)) return 'torso';
        return 'other';
      }
      at = at.parent === null ? undefined : byName.get(at.parent);
    }
    return 'other';
  });
  const owner = new Array<Owner>(n);
  for (let v = 0; v < n; v++) {
    let best = -1;
    let bw = -1;
    for (let k = 0; k < 4; k++) {
      const w = input.skin.weight[v * 4 + k]!;
      if (w > bw) {
        bw = w;
        best = input.skin.index[v * 4 + k]!;
      }
    }
    owner[v] = ownerOfBone[best] ?? 'other';
  }

  // --- the spine line, by height: the mapped chain's joints
  const chain = ['Hips', 'Spine', 'Spine1', 'Spine2', 'Neck']
    .map((id) => (input.mapped[id] === undefined ? undefined : byName.get(input.mapped[id]!)?.worldPosition))
    .filter((p): p is Point3 => p !== undefined)
    .map((p) => ({ h: dot(sub(p, F.origin), F.up), l: dot(sub(p, F.origin), F.lateral), d: dot(sub(p, F.origin), F.forward) }))
    .sort((a, b) => a.h - b.h);
  const axisAt = (h: number): { l: number; d: number } => {
    if (chain.length === 0) return { l: 0, d: 0 };
    if (h <= chain[0]!.h) return chain[0]!;
    for (let k = 0; k + 1 < chain.length; k++) {
      const a = chain[k]!;
      const b = chain[k + 1]!;
      if (h <= b.h) {
        const t = b.h > a.h ? (h - a.h) / (b.h - a.h) : 0;
        return { l: a.l + (b.l - a.l) * t, d: a.d + (b.d - a.d) * t };
      }
    }
    return chain[chain.length - 1]!;
  };
  // Body coordinates per vertex: lateral from the midline, height above the hip joints, depth from the spine line.
  const lat = new Float64Array(n);
  const hgt = new Float64Array(n);
  const dep = new Float64Array(n);
  for (let v = 0; v < n; v++) {
    const r = sub(pos(v), F.origin);
    const h = dot(r, F.up);
    const a = axisAt(h);
    hgt[v] = h;
    lat[v] = dot(r, F.lateral) - a.l;
    dep[v] = dot(r, F.forward) - a.d;
  }

  // --- faces, cloth, welded positions
  const I = input.indices;
  const faceCount = Math.floor(I.length / 3);
  const cloth = new Set(input.excludeFaces ?? []);
  const excluded = new Set(input.excludeVertices ?? []);
  const loose = new Set(input.looseVertices ?? []);
  const posId = new Int32Array(n);
  const ids = new Map<string, number>();
  for (let v = 0; v < n; v++) {
    const k = `${P[v * 3]},${P[v * 3 + 1]},${P[v * 3 + 2]}`;
    let id = ids.get(k);
    if (id === undefined) {
      id = ids.size;
      ids.set(k, id);
    }
    posId[v] = id;
  }
  // Face adjacency through welded positions, for the apex's connected component.
  const facesOfPos = new Map<number, number[]>();
  for (let f = 0; f < faceCount; f++) {
    for (let c = 0; c < 3; c++) {
      const p = posId[I[f * 3 + c]!]!;
      const list = facesOfPos.get(p);
      if (list === undefined) facesOfPos.set(p, [f]);
      else list.push(f);
    }
  }

  const regions: SoftTissueRegion[] = [];
  const unmeasured: { id: SoftTissueRegionId; why: string }[] = [];

  /**
   * The region from its faces: the ring, the feather, the root. `rho` (the
   * detector's ellipsoid parameter, 1 on its surface) adds a second ramp so
   * the feather also reaches 0 where the ellipsoid — not a face edge — bounds
   * the region; the feather is the smaller of the two. A face island with no
   * ring (a closed shell inside the region) takes the ellipsoid alone, or,
   * declared, the distance from the apex against the island's reach.
   */
  const finish = (
    id: SoftTissueRegionId,
    source: 'measured' | 'declared',
    zoneName: string | null,
    faceSet: number[],
    apexVertex: number,
    prominence: number,
    reading: string,
    rho?: (v: number) => number,
  ): SoftTissueRegion => {
    const kind = kindOfRegion(id);
    const tiers = SOFT_TISSUE_TIERS[kind];
    const ratio = prominence / T;
    const tier: SoftTissueTier = source === 'declared' ? 'strong' : ratio >= tiers.strong ? 'strong' : ratio >= tiers.weak ? 'weak' : 'none';
    const inFace = new Set(faceSet);
    const regionPos = new Set<number>();
    for (const f of faceSet) for (let c = 0; c < 3; c++) regionPos.add(posId[I[f * 3 + c]!]!);
    // The ring: a region position that any face outside the region also uses (cloth faces included).
    const ring = new Set<number>();
    for (const p of regionPos) if ((facesOfPos.get(p) ?? []).some((f) => !inFace.has(f))) ring.add(p);
    const vertices: number[] = [];
    for (let v = 0; v < n; v++) if (regionPos.has(posId[v]!)) vertices.push(v);
    // One representative point per welded position.
    const repr = new Map<number, Point3>();
    for (const v of vertices) if (!repr.has(posId[v]!)) repr.set(posId[v]!, pos(v));
    const ringPts = [...ring].map((p) => repr.get(p)!);
    const reprVertex = new Map<number, number>();
    for (const v of vertices) if (!reprVertex.has(posId[v]!)) reprVertex.set(posId[v]!, v);
    // Distance to the ring, per position (Infinity when the region has none).
    const dBOf = new Map<number, number>();
    for (const [p, q] of repr) {
      let dB = ring.has(p) ? 0 : Infinity;
      if (dB > 0) for (const r of ringPts) dB = Math.min(dB, Math.hypot(q.x - r.x, q.y - r.y, q.z - r.z));
      dBOf.set(p, dB);
    }
    // THE TIP is the region's surface CENTRE — the flesh farthest from its
    // ring — not the single most prominent vertex, which can sit ON the ring
    // (a glute's deepest point at the pelvis/thigh ownership edge, measured
    // 2026-09-24 on the tiger-woman and Prince: a leaf tipped there owned
    // almost nothing). With no ring (a closed shell) the prominent vertex stands.
    let centreVertex = apexVertex;
    if (ringPts.length > 0) {
      let best = -1;
      for (const [p, dB] of dBOf) {
        if (dB > best) {
          best = dB;
          centreVertex = reprVertex.get(p)!;
        }
      }
    }
    const apex = pos(centreVertex);
    let reach = 0;
    for (const q of repr.values()) reach = Math.max(reach, Math.hypot(q.x - apex.x, q.y - apex.y, q.z - apex.z));
    const featherOf = new Map<number, number>();
    // The plateau's ramp: a share of the prominence, never under 2% of the torso (a flat mass still blends).
    const ramp = Math.max(SOFT_TISSUE_PLATEAU * prominence, 0.02 * T);
    for (const [p, q] of repr) {
      const dA = Math.hypot(q.x - apex.x, q.y - apex.y, q.z - apex.z);
      const dB = dBOf.get(p)!;
      // With a ring, the ring IS the boundary (a face the ellipsoid cut is not
      // in the region, so its inside corners are ring): 0 there, 1 a ramp inside it.
      // A ringless island takes the ellipsoid (detected) or its own reach (declared).
      const f = ringPts.length > 0
        ? (dB === 0 ? 0 : smoothstep(dB / ramp))
        : rho !== undefined
          ? smoothstep(1 - rho(reprVertex.get(p)!))
          : smoothstep(1 - dA / (reach || 1));
      featherOf.set(p, f);
    }
    const feather = vertices.map((v) => featherOf.get(posId[v]!)!);
    const ringCentre = ringPts.length > 0 ? scale(ringPts.reduce((s, r) => add(s, r), { x: 0, y: 0, z: 0 }), 1 / ringPts.length) : apex;
    // THE BASE of the mass: back from the ring's centroid, never deeper than 0.8 of the ring's own depth from the spine.
    const outward = kind === 'glute' ? -1 : 1;
    let ringDepth = 0;
    for (const p of ring) ringDepth += outward * dep[reprVertex.get(p)!]!;
    ringDepth = ring.size > 0 ? ringDepth / ring.size : 0;
    const back = ringPts.length > 0 ? Math.max(0, Math.min(SOFT_TISSUE_PIVOT_DEPTH * prominence, 0.8 * ringDepth)) : 0;
    const root = add(ringCentre, scale(F.forward, -outward * back));
    // Radius: half the region's smaller in-plane extent (lateral × vertical), as the flesh's own size.
    let lMin = Infinity, lMax = -Infinity, hMin = Infinity, hMax = -Infinity;
    for (const v of vertices) {
      lMin = Math.min(lMin, lat[v]!);
      lMax = Math.max(lMax, lat[v]!);
      hMin = Math.min(hMin, hgt[v]!);
      hMax = Math.max(hMax, hgt[v]!);
    }
    const radius = vertices.length > 0 ? Math.min(lMax - lMin, hMax - hMin) / 2 : 0;
    return {
      id,
      kind,
      source,
      zoneName,
      tier,
      prominence,
      prominenceRatio: ratio,
      apex,
      root,
      vertices,
      faces: faceSet,
      feather,
      ringPositions: ring.size,
      clothFacesSkipped: faceSet.length === 0 ? 0 : 0,
      radius,
      reading,
    };
  };

  for (const id of SOFT_TISSUE_REGIONS) {
    const kind = kindOfRegion(id);
    const s = sideOfRegion(id);
    const sign = kind === 'glute' ? -1 : 1; // glutes stand out BACKWARD
    const declared = input.declared?.[id];
    if (declared !== undefined) {
      const faces = [...new Set(declared.faces)].filter((f) => f >= 0 && f < faceCount && !cloth.has(f)).sort((a, b) => a - b);
      if (faces.length === 0) {
        unmeasured.push({ id, why: `zone "${declared.zoneName}" has no faces this body carries (or all are cloth)` });
        continue;
      }
      let apex = I[faces[0]! * 3]!;
      for (const f of faces) for (let c = 0; c < 3; c++) {
        const v = I[f * 3 + c]!;
        if (sign * dep[v]! > sign * dep[apex]!) apex = v;
      }
      const r = finish(id, 'declared', declared.zoneName, faces, apex, 0, '');
      const minDepth = Math.min(...r.vertices.map((v) => sign * dep[v]!));
      const prom = sign * dep[apex]! - minDepth;
      regions.push({
        ...r,
        prominence: prom,
        prominenceRatio: prom / T,
        clothFacesSkipped: declared.faces.length - faces.length,
        reading: `declared by zone "${declared.zoneName}": ${r.vertices.length} vertices, apex ${(prom * 100).toFixed(1)} cm proud of its ring`,
      });
      continue;
    }

    // --- detection ---
    const band =
      kind === 'breast' ? [0.45 * T, 0.95 * T] : kind === 'belly' ? [0.15 * T, 0.5 * T] : [-0.45 * T, 0.05 * T];
    // Breast and belly: torso-owned flesh. Glute: torso- or thigh-owned (the
    // buttock spans the pelvis/thigh boundary even after ADR-029's re-own),
    // minus loose flesh (a coat tail the thighs also own) and never the waist
    // BELT: the glute band stops at the hip joints (+0.05 T), because a belt
    // at the waist is the most posterior torso flesh above them (Prince, the
    // tiger-woman — measured 2026-09-24).
    const owns = (v: number): boolean =>
      !excluded.has(v) && (owner[v] === 'torso' || (kind === 'glute' && owner[v] === 'leg' && !loose.has(v)));
    const onSide = (v: number): boolean => (s === 0 ? Math.abs(lat[v]!) < 0.25 * T : s * lat[v]! > Math.max(0.03 * T, 0.2 * F.hipWidth));
    let apex = -1;
    for (let v = 0; v < n; v++) {
      if (!owns(v) || hgt[v]! < band[0]! || hgt[v]! > band[1]! || !onSide(v) || !(sign * dep[v]! > 0)) continue;
      if (apex < 0 || sign * dep[v]! > sign * dep[apex]!) apex = v;
    }
    if (apex < 0) {
      unmeasured.push({ id, why: `no ${kind === 'glute' ? 'posterior' : 'anterior'} torso flesh in the ${kind} band on this side` });
      continue;
    }
    const aL = lat[apex]!;
    const aH = hgt[apex]!;
    const aD = sign * dep[apex]!;
    // The wall: the SURFACE in the apex's column (the most prominent flesh per
    // height bin — never the torso's side or back, which the column also cuts),
    // at its shallowest toward the neighbouring hollow (breast: down to the fold;
    // glute: up to the lumbar hollow; belly: up to the ribcage).
    const colHalf = 0.06 * T;
    const [c0, c1] = kind === 'breast' ? [aH - 0.35 * T, aH] : [aH, 0.55 * T];
    const bin = 0.02 * T;
    const bins = Math.max(1, Math.ceil((c1 - c0) / bin));
    const surface = new Float64Array(bins).fill(-Infinity);
    for (let v = 0; v < n; v++) {
      if (!owns(v) || Math.abs(lat[v]! - aL) > colHalf || hgt[v]! < c0 || hgt[v]! > c1) continue;
      const b = Math.min(bins - 1, Math.floor((hgt[v]! - c0) / bin));
      surface[b] = Math.max(surface[b]!, sign * dep[v]!);
    }
    let wall = aD;
    let wallH = aH;
    for (let b = 0; b < bins; b++) {
      if (surface[b]! > 0 && surface[b]! < wall) {
        wall = surface[b]!;
        wallH = c0 + (b + 0.5) * bin;
      }
    }
    const prominence = aD - wall;
    // The ellipsoid: lateral to the midline (sided) or a torso-fraction (belly),
    // vertical from the apex to the wall's height (at least a floor), depth to the wall.
    const rx = s === 0 ? 0.22 * T : Math.max(Math.abs(aL), 0.06 * T);
    const ry = Math.max(Math.abs(aH - wallH), kind === 'belly' ? 0.18 * T : 0.1 * T) * 1.1;
    // Depth reaches a quarter-prominence behind the wall (the fold's own crease), and
    // the ellipsoid's surface IS that limit, so the feather is 0 wherever depth bounds it.
    const rz = Math.max(prominence * 1.25, 0.03 * T);
    const rho = (v: number): number => {
      const ex = (lat[v]! - aL) / rx;
      const ey = (hgt[v]! - aH) / ry;
      const ez = Math.max(0, aD - sign * dep[v]!) / rz;
      return Math.sqrt(ex * ex + ey * ey + ez * ez);
    };
    // Every face inside, not only the apex's component: a garment shell (a bra
    // cup, a breastplate) and the flesh under it are ONE mass — the flesh must
    // move with the cup or it pokes through. Arms, hair and tails are out by owner.
    // A GLUTE reaches at most GLUTE_REACH_BELOW under its apex. The ellipsoid's vertical
    // radius is the reach UP to the lumbar hollow, and applied DOWNWARD it ran as far into
    // the thigh: Jim's right glute (2026-09-29) took 248 thigh-owned vertices of 348, its
    // centre — the leaf's tip — sank 0.34 T below the hip, and the leaf tore the thigh.
    // Census of 12 bodies: only the broken ones move (Jim 0.34 T → −0.17 T, Gabriel
    // 0.49 → −0.05, Hamish 0.54 → 0.29); the rest are unchanged. (A fold search was
    // tried and refused: armour plates and coarse meshes put two surfaces or none in
    // the rear profile — the tiger warrior's alternates 11 / 7 cm between 1 cm bins —
    // and a plateau-centred apex moved healthy bodies; the cap is a length, not a
    // reading of the surface.)
    const floorH = kind === 'glute' ? aH - GLUTE_REACH_BELOW * T : -Infinity;
    const inside = (v: number): boolean => owns(v) && (s === 0 || s * lat[v]! >= 0) && rho(v) < 1 && hgt[v]! >= floorH;
    const faces: number[] = [];
    let clothHits = 0;
    for (let f = 0; f < faceCount; f++) {
      if (!inside(I[f * 3]!) || !inside(I[f * 3 + 1]!) || !inside(I[f * 3 + 2]!)) continue;
      if (cloth.has(f)) clothHits++;
      else faces.push(f);
    }
    const grown = { faces, cloth: clothHits };
    if (grown.faces.length === 0) {
      unmeasured.push({ id, why: `the ${kind} apex has no faces around it that pass the region's gates` });
      continue;
    }
    const r = finish(id, 'measured', null, grown.faces, apex, prominence, '', rho);
    regions.push({
      ...r,
      clothFacesSkipped: grown.cloth,
      reading:
        `${r.tier === 'none' ? 'not offered' : r.tier === 'weak' ? 'offered (weak)' : 'offered'} — apex ${(prominence * 100).toFixed(1)} cm proud of the ` +
        `${kind === 'breast' ? 'fold below it' : kind === 'glute' ? 'lumbar hollow above it' : 'ribcage column above it'} ` +
        `(${(r.prominenceRatio * 100).toFixed(1)}% of the torso), ${r.vertices.length} vertices` +
        (grown.cloth > 0 ? `, ${grown.cloth} cloth faces left out` : ''),
    });
  }
  return { frame: F, frameRefusal: null, regions, unmeasured };
}
